import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../01_atoms/Button';
import Spinner from '../../01_atoms/Spinner';
import * as breakpoints from '../../../utils/breakpoint';
import styles from './index.module.scss';

class Paginator extends React.Component {
  PERIOD = '...';

  constructor(props) {
    super(props);

    this.state = {
      isFetching: false,
      isMobile: false,
    };

    this.onWindowResize = this.onWindowResize.bind(this);
  }

  componentDidMount() {
    this.onWindowResize();
    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  // Render different indicators based on the window size
  onWindowResize() {
    const { isMobile } = this.state;

    if (breakpoints.isUp('md') && isMobile) {
      this.setState({ isMobile: false });
    }

    if (breakpoints.isDown('md') && !isMobile) {
      this.setState({ isMobile: true });
    }
  }

  onPageChangeRequest = (indicator) => {
    const { onPageChange } = this.props;

    // don't trigger page change on current page indicator click
    if (typeof indicator !== 'number' && indicator.active) {
      return;
    }

    const page = typeof indicator === 'number' ? indicator : indicator.jumpTo || indicator.page;

    this.setState({ isFetching: true });
    onPageChange(page).then(() => {
      this.setState({ isFetching: false });
    });
  };

  PageIndicators = () => {
    const { totalPages, currentPage } = this.props;
    const { isMobile } = this.state;

    const indicatorGenerator = (startIndex) =>
      [startIndex - 1, startIndex, startIndex + 1].map((i) => {
        const currentIndex = currentPage + i;

        return {
          page: currentIndex,
          active: currentIndex === currentPage,
          clickable: true,
          isBoundary: currentIndex === 1 || currentIndex === totalPages,
        };
      });

    let indicatorMiddle = indicatorGenerator(0);

    // make sure to render first page + up to 2 more after it if on first page
    if (currentPage === 1) {
      indicatorMiddle = indicatorGenerator(1);
    }
    // make sure to render last page + up to 2 more before it if on last page
    if (currentPage === totalPages) {
      indicatorMiddle = indicatorGenerator(-1);
    }

    // If we are on desktop ...
    if (!isMobile) {
      // ... add periods to either before or after the currently-displayed trio of pages,
      if (currentPage > 3) {
        indicatorMiddle.unshift({ page: this.PERIOD, jumpTo: currentPage - 2 });
      }
      if (currentPage < totalPages - 2) {
        indicatorMiddle.push({ page: this.PERIOD, jumpTo: currentPage + 2 });
      }

      // ... always add the first and last pages to render,
      indicatorMiddle.unshift({ page: 1, active: currentPage === 1 });
      indicatorMiddle.push({ page: totalPages, active: currentPage === totalPages });

      // ... and therefore filter out duplicate generated boundaries (1 and totalPages),
      indicatorMiddle = indicatorMiddle.filter((indicator) => !indicator.isBoundary);

      // ... and any generated artifacts that are greater than total amount of pages or zero.
      indicatorMiddle = indicatorMiddle.filter(
        (indicator) =>
          indicator.page === this.PERIOD || (indicator.page >= 1 && indicator.page <= totalPages),
      );
    }

    return indicatorMiddle.map((indicator, i) => (
      <div
        key={`${indicator.page}-${i}`}
        className={`page-indicator ${indicator.active ? 'active' : ''}`}
        onClick={() => this.onPageChangeRequest(indicator)}
        onKeyDown={() => this.onPageChangeRequest(indicator)}
      >
        <p>{indicator.page}</p>
      </div>
    ));
  };

  render() {
    const { totalPages, currentPage } = this.props;
    const { isFetching } = this.state;

    return (
      <div className={`paginator ${styles.paginator}`}>
        <div className="container">
          <div className="d-flex justify-content-center justify-content-md-between">
            <div className={`d-none d-md-block ${currentPage === 1 ? 'invisible' : ''}`}>
              <Button
                type="secondary"
                isOutlined
                isDisabled={isFetching}
                onClick={() => this.onPageChangeRequest(currentPage - 1)}
                className="paginator--button paginator-prev--button"
              >
                &laquo; Previous
              </Button>
            </div>

            <div className="text-center">
              {isFetching ? (
                <div className="spinner-container">
                  <Spinner color="turquoise" />
                </div>
              ) : (
                <this.PageIndicators />
              )}
            </div>

            <div className={`d-none d-md-block ${currentPage === totalPages ? 'invisible' : ''}`}>
              <Button
                type="secondary"
                isOutlined
                isDisabled={isFetching}
                onClick={() => this.onPageChangeRequest(currentPage + 1)}
                className="paginator--button paginator-next--button"
              >
                Next &raquo;
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Paginator.propTypes = {
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default Paginator;
